import { PropsWithChildren, ReactNode, useMemo, useState } from 'react';
import { twMerge } from 'tailwind-merge';

import { InfoRevealContext, InfoRevealState, useInfoReveal } from '~/routes/remote-mic/components/info-reveal-context';

// Explanatory prose under a control is gone from the phone; in its place is an `i` that reveals the
// same line in place. A phone has no hover, so the TV's `title` tooltips cannot carry it - the line
// has to be somewhere a finger can reach, and a line always on screen is what made the panels
// overlap in the first place.
//
// Only one is open at a time, so pressing a second `i` closes the first rather than growing the
// panel twice. Opening a sheet clears it for the same reason.

export function InfoRevealProvider({ children }: PropsWithChildren) {
  const [openKey, setOpenKey] = useState<string | null>(null);

  const value = useMemo<InfoRevealState>(
    () => ({
      openKey,
      toggle: (key) => setOpenKey((current) => (current === key ? null : key)),
      clear: () => setOpenKey(null),
    }),
    [openKey],
  );

  return <InfoRevealContext.Provider value={value}>{children}</InfoRevealContext.Provider>;
}

interface ButtonProps {
  infoKey: string;
  className?: string;
}

export function InfoButton({ infoKey, className }: ButtonProps) {
  const { toggle } = useInfoReveal();

  return (
    <button
      type="button"
      aria-label="What's this?"
      data-test={`info-${infoKey}`}
      // Two of these sit inside rows that act on tap - Reset microphone and Reset players - so
      // without this the press reaches the row and resets what it was asking about.
      onClick={(event) => {
        event.stopPropagation();
        toggle(infoKey);
      }}
      // The hit box is 44px; the glyph inside it is 20px. The negative margin cancels the padding
      // so a row that gains an `i` does not get taller or re-flow around it.
      className={twMerge(
        '-m-3 flex size-11 shrink-0 items-center justify-center border-none bg-transparent',
        className,
      )}>
      <span
        aria-hidden
        className="flex size-5 items-center justify-center rounded-full text-[0.75rem] leading-none font-bold text-white/80 shadow-[inset_0_0_0_1px_rgba(255,255,255,0.5)]">
        i
      </span>
    </button>
  );
}

interface LineProps {
  infoKey: string;
  children: ReactNode;
  // The gradient needs a darker plate to stay legible; a sheet is already near-black, so there the
  // plate is a lift rather than a shade.
  surface?: 'gradient' | 'ground';
}

export function InfoLine({ infoKey, children, surface = 'ground' }: LineProps) {
  const { openKey } = useInfoReveal();
  if (openKey !== infoKey) return null;

  return (
    <span
      data-test={`info-line-${infoKey}`}
      className={twMerge(
        'rounded-xl px-3 py-2.5 text-[0.875rem] leading-[1.4] text-white/90',
        surface === 'gradient' ? 'bg-black/45' : 'bg-white/7',
      )}>
      {children}
    </span>
  );
}
